<template>
  <div class="message-list-root">
    <div ref="container" class="chat-scroll-wrapper" :class="{ 'is-scroll-hover': scrollHover }" @mouseenter="scrollHover = true" @mouseleave="scrollHover = false" @scroll="handleScroll" @click="handleContainerClick" @wheel="handleUserScrollIntent" @touchmove="handleUserScrollIntent">
      <div class="message-list-container">
      <template v-if="visibleMessages.length === 0">
        <div v-if="!isConnected" class="empty-state">
          <div class="empty-icon">
            <i :class="connectionIcon"></i>
          </div>
          <el-tag :type="connectionStatusType" size="small" effect="dark">{{ connectionStatusText }}</el-tag>
        </div>
        <CosmoClawHome v-else />
      </template>
      <template v-else>
       <template v-for="(group, gidx) in groupedMessages">
         <CosmoClawMessageItem
            v-if="group.type === 'message'"
            :key="group.data.id"
            :id="group.data.id"
            :type="group.data.type"
            :content="group.data.content"
            :attachments="group.data.attachments"
            :timestamp="group.data.timestamp"
            :is-streaming="group.data.isStreaming"
            :is-history="group.data.isHistory"
            :is-error="group.data.isError"
            :tool-name="group.data.toolName"
            :tool-call-id="group.data.toolCallId"
            :phase="group.data.phase"
            :args="group.data.args"
            :result="group.data.result"
            :category="group.data.category"
            :msg-data="group.data.data"
            :msg-items="group.data.items"
            :history-data="group.data.historyData"
            :msg-parent="group.data.parent"
            :media-urls="group.data._mediaUrls"
            :rich-html="group.data.richHtml"
            :wide-tool-card="wideToolCard"
          />
         <CosmoClawProcessTrace
           v-else
           :key="group.traceKey"
           :msgs="group.msgs"
           :is-live="sessionIsLive && gidx === lastTraceIdx"
           :answer-started="gidx === lastTraceIdx && lastTraceAnswerStarted"
           :is-last="gidx === lastTraceIdx"
           :is-wide="wideToolCard"
           @open-media="onOpenTraceMedia"
           @open-detail="onOpenTraceDetail"
           @open-text="onOpenTraceText"
       />
       </template>

        <!-- 处理中状态行：发送后到首条思考 / 工具 / 正文出现前的空白等待期 -->
        <div v-if="showProcessingStatus" class="processing-status">
          <span class="processing-status__spinner">
            <span v-for="n in 9" :key="n" class="processing-status__cube"></span>
          </span>
          <span class="processing-status__label">
            <transition name="processing-fade" mode="out-in">
              <span :key="processingText" class="processing-status__text">{{ processingText }}</span>
            </transition>
            <span v-if="processingElapsed >= 1" class="processing-status__elapsed">{{ processingElapsedText }}</span>
          </span>
        </div>
      </template>
       <div ref="messagesEnd"></div>
      </div>

      <transition name="fade">
        <div v-if="showScrollButton" class="scroll-to-bottom" @click="scrollToBottom">
          <i class="el-icon-bottom"></i>
        </div>
      </transition>
    </div>

    <!-- 刻度尺作为外层 root 的同级元素，absolute 定位紧贴右侧，不随内容滚动 -->
    <CosmoClawMessageRail
      v-if="showRail"
      class="msg-rail"
      :nodes="railNodes"
      :active-index="railActiveIndex"
      :mounted-keys="railMountedKeys"
      @select="$emit('rail-select', $event)"
    />

    <!-- 工具调用详情：全屏浮层，挂在外层 root 下（滚动容器之外），避免受 transform / overflow 影响 -->
    <CosmoClawActivityOverlay
      v-if="detailMsg"
      :msg="detailMsg"
      @close="detailMsg = null"
    />

    <!-- 思考 / 过程叙述全文：居中文本浮层（同目标项目 Activity 浮层形态） -->
    <CosmoClawTextOverlay
      v-if="textDetail"
      :text="textDetail.text"
      @close="textDetail = null"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import CosmoClawMessageItem from './CosmoClawMessageItem.vue'
import CosmoClawProcessTrace from './CosmoClawProcessTrace.vue'
import CosmoClawHome from './CosmoClawHome.vue'
import CosmoClawMessageRail from './CosmoClawMessageRail.vue'
import CosmoClawActivityOverlay from './CosmoClawActivityOverlay.vue'
import CosmoClawTextOverlay from './CosmoClawTextOverlay.vue'
import { computeProcessIds, isProcessMessage, pendingProcessId, findLiveUnmarkedNarration, looksLikeBody } from '../utils/processTrace'
import { wsTrace } from '@/utils/wsDebugTrace'

// 兜底超时（ms）：工具后无标记叙述若一直不"像正文"（没超字数也没 markdown 结构），
// 又迟迟等不到下一条工具调用，超过这个时长就按正文落定到气泡，避免一直隐藏卡死。
// 正常路径靠 looksLikeBody 的内容阈值触发，这个 timer 只是安全网。
const NARRATION_FALLBACK_MS = 10000

// 处理中状态行：等待期轮换展示的文案池（与客户端 chat.processing.* 取一组精简版）
const PROCESSING_MESSAGES = [
  '预热中...',
  '思考中...',
  '推理中...',
  '反思中...',
  '仔细琢磨...',
  '梳理思路...',
  '斟酌中...',
  '处理中...',
  '整理线索...',
  '马上就好...'
]
// 文案轮换间隔（ms）
const PROCESSING_ROTATE_MS = 10000

export default {
  name: 'CosmoClawMessageList',
  components: { CosmoClawMessageItem, CosmoClawProcessTrace, CosmoClawHome, CosmoClawMessageRail, CosmoClawActivityOverlay, CosmoClawTextOverlay },
  props: {
    // 宽屏形态:透传给工具调用卡片,使其以半宽展示(抽屉放大态 / 数字人会话页)
    wideToolCard: {
      type: Boolean,
      default: false
    },
    // 消息导航刻度尺 props（方案 B）
    railNodes: { type: Array, default: () => [] },
    railActiveIndex: { type: Number, default: null },
    railMountedKeys: { type: Object, default: () => ({}) },
    showRail: { type: Boolean, default: false },
    // 会话搜索关键词：命中的消息正文在渲染后高亮
    searchKeyword: { type: String, default: '' }
  },
  emits: ['rail-select'],
  data() {
    return {
      // 滚动容器是否处于鼠标悬浮状态，用于控制滚动条显隐
      scrollHover: false,
      autoScroll: true,
      showScrollButton: false,
      scrollTimer: null,
      // rAF 逐帧自动贴底句柄;以及收起巡检的节流时间戳(避免每帧都做几何测量)
      autoScrollRaf: null,
      lastCollapseCheckAt: 0,
      lastMsgCount: 0,
      // 上一次处理过的 scrollTop：只凭"位置是否向上移动"判断用户接管。
      // 不再用"距底 > 50px"——内容一次性长一大块（表格 / 富卡片插入）时，
      // 我们自己贴底的写操作与其 scroll 事件之间会隔着这次增长，被误判成用户上滚，
      // 自动跟随会被永久关掉。
      lastScrollTop: 0,
      // 用户主动接管滚动的"锁"。一旦用户向上滚动置为 true,
      // 此后所有自动贴底源(rAF / messages watch / 收起补贴底)全部让位,
      // 直到用户自己把视图滚回底部附近才解锁。
      // 这是消除"向上滚被流式 chunk 反复拽回底部"拉扯抖动的关键。
      userScrolling: false,
      // touchmove 场景记录上一次 scrollTop,用于判断触摸滑动方向(向上滑 → 接管)
      lastTouchScrollTop: 0,
      // 工具后无标记叙述的"缓冲判定"状态:
      // bufferedNarrationId —— 正在隐藏等判定的叙述消息 id(既不进气泡也不进容器,分组时跳过);
      // lockedBodyIds —— 被判定为"最终正文"的叙述消息 id(可先在气泡里流式):
      //                  仅用于阻止已判定过的叙述被再次缓冲隐藏,不参与分组;
      //                  若它后面又跟来工具调用,仍由反向扫描收进同一条 trace,一轮不裂卡。
      // 落定为正文的触发:looksLikeBody(内容超阈值或 markdown 强结构) 或兜底超时 NARRATION_FALLBACK_MS;
      // 落定为过程的触发:下一条工具调用到达(候选自然变 null → 反向扫描收进容器)。
      bufferedNarrationId: null,
      lockedBodyIds: [],
      narrationBufferTimer: null,
      // 详情浮层当前查看的工具消息；null 表示浮层关闭
      detailMsg: null,
      // 全文浮层当前查看的思考 / 过程叙述（{ text }）；null 表示浮层关闭
      textDetail: null,
      // 处理中状态行：已用时（秒）与当前文案下标
      processingElapsed: 0,
      processingMessageIndex: 0,
      // 处理中状态行的计时器 / 文案轮换计时器
      processingTimer: null,
      processingRotateTimer: null
    }
  },
  computed: {
    ...mapState('cosmoClaw', ['messages', 'isStreaming', 'showTools', 'currentRunId', 'isSwitchingSession', 'socketState', 'isHandshaked', 'localDemoMode', 'currentSessionKey']),
    ...mapState('cosmoClaw', { sessionIsLive: 'sessionIsLive' }),
    isConnected() {
      return this.socketState === 'CONNECTED' && this.isHandshaked
    },
    // 渲染层防御：只渲染当前会话的消息；
    // 旧会话泄漏到 flat state 的消息（_sessionKey 不匹配）一律不展示
    visibleMessages() {
      const sk = this.currentSessionKey
      if (!sk) return this.messages
      return this.messages.filter((m) => !m._sessionKey || m._sessionKey === sk)
    },
    connectionStatusType() {
      if (this.socketState === 'CONNECTING') return 'warning'
      if (this.socketState === 'FAILED') return 'danger'
      return 'info'
    },
    connectionStatusText() {
      if (this.socketState === 'CONNECTING') return this.$t('workbench.connecting')
      if (this.socketState === 'FAILED') return this.$t('workbench.connectFailed')
      return this.$t('workbench.disconnected')
    },
    connectionIcon() {
      if (this.socketState === 'CONNECTING') return 'el-icon-loading'
      if (this.socketState === 'FAILED') return 'el-icon-circle-close'
      return 'el-icon-warning-outline'
    },
    /**
     * 本轮最终正文是否已经开始输出。
     * 有过程 trace 时只在最后一条 trace 之后看；否则从最后一条用户消息之后看。
     * 正文一开始就不再展示处理中状态行（过程/工具阶段则一直展示）。
     */
    answerStarted() {
      const groups = this.groupedMessages
      let lastUserGroup = -1
      for (let i = groups.length - 1; i >= 0; i--) {
        if (groups[i].type === 'message' && groups[i].data.type === 'user') {
          lastUserGroup = i
          break
        }
      }
      const traceIdx = this.lastTraceIdx
      const from = traceIdx > lastUserGroup ? traceIdx + 1 : lastUserGroup + 1
      for (let i = from; i < groups.length; i++) {
        const g = groups[i]
        const msg = g.type === 'message' ? g.data : null
        if (!msg || msg.type !== 'assistant') continue
        if (typeof msg.content === 'string' && msg.content.trim()) return true
      }
      return false
    },
    // 从发送后一直展示到最终正文开始输出：思考 / 工具调用的整个过程都保留状态与计时
    showProcessingStatus() {
      if (this.localDemoMode) return false
      if (!this.isStreaming && !this.sessionIsLive) return false
      return !this.answerStarted
    },
    processingText() {
      return PROCESSING_MESSAGES[this.processingMessageIndex] || PROCESSING_MESSAGES[0]
    },
    // 计时起点：本轮用户消息的时间戳（与客户端一致），时间戳缺失时退回当前时刻
    processingStartTime() {
      const msgs = this.visibleMessages
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].type === 'user') {
          return typeof msgs[i].timestamp === 'number' ? msgs[i].timestamp : null
        }
      }
      return null
    },
    // 满 1 秒才展示；不足 60 秒展示「12.1s」，超过展示「1:11」
    processingElapsedText() {
      const s = this.processingElapsed
      if (s < 60) return `${s.toFixed(1)}s`
      return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`
    },
    /**
     * 过程消息 id 集合（思考 / 工具调用 / 过程叙述）。
     *
     * 两个来源叠加：
     *   1. computeProcessIds 反向扫描（结构判定：叙述后面还有没有过程内容）—— 唯一权威；
     *   2. pendingProcessId —— 运行中最后一条带【分析过程】标记的叙述立刻进容器。
     *
     * 刻意不再剔除 lockedBodyIds：分组权威交给结构扫描。一条叙述只要其后还有过程内容，
     * 就必须进同一条 trace；否则内容启发式（looksLikeBody / 超时落锁）会把中间的叙述
     * 剔出容器，在两段过程之间形成断点，一轮裂成多条 trace —— 这正是"实时会裂成多条、
     * 刷新看历史只有一条"的根因。lockedBodyIds 现在只用于阻止已判定过的叙述被再次缓冲隐藏。
     *
     * 纯派生、只读，不修改任何消息对象。
     */
    processIds() {
      const ids = computeProcessIds(this.visibleMessages)
      const pendingId = pendingProcessId(this.visibleMessages, this.sessionIsLive)
      if (pendingId != null) ids.add(pendingId)
      return ids
    },
    /**
     * 运行中需要做"缓冲判定"的那条无标记工具后叙述（见 findLiveUnmarkedNarration）。
     *
     * 隐藏期间它既不进气泡也不进容器（groupedMessages 里跳过）；
     *   - 下一条工具调用到达 → 该消息不再"最后" → 候选自然变 null → 由反向扫描
     *     把它收进容器（从未在容器外出现过，不闪、不漏）；
     *   - 内容越过阈值（looksLikeBody）→ 落锁 lockedBodyIds → 先在气泡里流式
     *     （若之后又跟来工具调用，反向扫描会把它收进同一条 trace）；
     *   - 兜底超时 / 回合结束 → 同样落定为正文/气泡，避免一直隐藏。
     */
    bufferedNarrationCandidate() {
      if (!this.sessionIsLive || !this.showTools) return null
      const msg = findLiveUnmarkedNarration(this.visibleMessages)
      if (!msg || msg.id == null) return null
      if (this.lockedBodyIds.indexOf(msg.id) !== -1) return null
      return msg
    },
    /** 缓冲中的叙述内容是否已"像最终正文"（超字数或 markdown 强结构）。翻转 true 即落锁到气泡。 */
    bufferedNarrationLooksBody() {
      const c = this.bufferedNarrationCandidate
      return !!(c && looksLikeBody(typeof c.content === 'string' ? c.content : ''))
    },
    /**
     * 把消息流切成"过程 trace"与"普通消息"两类。
     *
     * 过程 trace = 一段连续的（思考 / 工具调用 / 中间态叙述）。它是一条完整的过程链，
     * 所以合并成一条可开合的列表；正式结论、用户消息等一律照旧走 MessageItem 渲染，
     * 渲染路径与本次改动前完全一致。
     */
    groupedMessages() {
      const msgs = this.visibleMessages
      if (!this.showTools) {
        return msgs
          .filter(m => m.type !== 'toolCall' && m.type !== 'tool')
          .map(m => ({ type: 'message', data: m }))
      }

      const processIds = this.processIds
      const groups = []
      let trace = null
      const flushTrace = () => {
        if (trace) {
          groups.push(trace)
          trace = null
        }
      }

      for (let i = 0; i < msgs.length; i++) {
        const msg = msgs[i]
        // 缓冲判定中的叙述:窗口内先不渲染(既不进气泡也不进容器),判定落定后一次性出现,
        // 避免"先闪气泡再进容器"或"先在容器里流完正文再跳出"两个方向的跳变
        if (msg.id != null && msg.id === this.bufferedNarrationId) continue
        if (isProcessMessage(msg, processIds)) {
          if (!trace) {
            trace = { type: 'trace', traceKey: 'trace-' + (msg.id || i), msgs: [] }
          }
          trace.msgs.push(msg)
          continue
        }
        flushTrace()
        groups.push({ type: 'message', data: msg })
      }
      flushTrace()
      return groups
    },
    // 最后一个过程 trace —— 只有它算"正在进行的这一轮"，据此决定默认展开
    lastTraceIdx() {
      const groups = this.groupedMessages
      for (let i = groups.length - 1; i >= 0; i--) {
        if (groups[i].type === 'trace') return i
      }
      return -1
    },
    /**
     * 正文气泡是否已经开始输出（最后一条 trace 之后出现了非过程的 assistant 内容）。
     * 一旦开始，过程卡片就不再算"执行中"：头部图标停止旋转。
     */
    lastTraceAnswerStarted() {
      const groups = this.groupedMessages
      const idx = this.lastTraceIdx
      if (idx < 0) return false
      for (let i = idx + 1; i < groups.length; i++) {
        const g = groups[i]
        const msg = g.type === 'message' ? g.data : null
        if (!msg || msg.type !== 'assistant') continue
        if (typeof msg.content === 'string' && msg.content.trim()) return true
      }
      return false
    }
  },
  watch: {
    // 进入 / 退出空白等待期时启停计时与文案轮换
    showProcessingStatus: {
      immediate: true,
      handler(val) {
        if (val) this.startProcessingStatus()
        else this.stopProcessingStatus()
      }
    },
    visibleMessages(val) {
      if (process.env.NODE_ENV !== 'production') {
        const flat = this.messages || []
        const visible = val || []
        const lastType = visible.length ? visible[visible.length - 1].type : ''
        // 流式增量会频繁触发，仅在「条数/类型/会话」发生结构变化时记录，避免刷掉 trace 缓冲
        const sig = [
          flat.length,
          visible.length,
          lastType,
          this.currentSessionKey || '',
          this.$store.state.cosmoClaw.currentAgentId || ''
        ].join('|')
        if (sig === this._renderSig) return
        this._renderSig = sig
        const s = this.$store.state.cosmoClaw
        const agentData = s.agentData || {}
        const perAgent = {}
        Object.keys(agentData).forEach((id) => {
          const ad = agentData[id] || {}
          const sk = ad.currentSessionKey || ''
          const list = (ad.sessionMessages && ad.sessionMessages[sk]) || []
          perAgent[id] = {
            currentSessionKey: sk,
            sessionMessageCount: list.length
          }
        })
        wsTrace('render', {
          flatMessages: flat.length,
          visible: visible.length,
          currentAgentId: s.currentAgentId,
          currentSessionKey: this.currentSessionKey,
          flatTypes: flat.map((m) => m.type),
          visibleTypes: visible.map((m) => m.type),
          visibleSessionKeys: visible.map((m) => m._sessionKey || ''),
          perAgent
        })
      }
    },
    messages(val, oldVal) {
      const newLen = val ? val.length : 0
      if (oldVal && val !== oldVal) {
        this.$nextTick(() => this.cleanupOrphanThinkingToggles())
      }
      if (newLen !== this.lastMsgCount) {
        const lastMsg = val && val.length > 0 ? val[val.length - 1] : null
        const isNewUserMsg = lastMsg && lastMsg.type === 'user'
        // 用户发了新问题(user 消息):无条件恢复跟随并解除用户接管锁——这是用户的显式意图。
        if (isNewUserMsg) {
          this.userScrolling = false
          this.autoScroll = true
          this.showScrollButton = false
        } else if (!this.userScrolling) {
          // 仅当用户未主动接管滚动时,流式新增内容才允许自动跟随。
          // 若用户正在向上翻看历史(userScrolling=true),绝不覆盖其滚动位置。
          if (oldVal && val !== oldVal) {
            this.autoScroll = true
            this.showScrollButton = false
          }
        }
        this.lastMsgCount = newLen
        if (this.autoScroll && !this.userScrolling) {
          this.$nextTick(() => this.scrollToBottom())
        }
      }
      // 消息列表变化时(如对话框收起再打开触发整体重渲染),恢复已展示的卡片状态
      if (oldVal !== val) {
        this.$nextTick(() => this.rehydrateCeoStrategyCards())
        // v-html 重渲染会清掉已插入的高亮，重新包一次
        this.scheduleKeywordHighlight()
      }
    },
    searchKeyword() {
      // 关键词变化 = 新的一次定位机会，允许再次滚动到首个命中
      this._kwScrollDone = false
      this.scheduleKeywordHighlight()
    },
    currentSessionKey() {
      // 切换会话后重新允许定位首个命中（关键词相同也要滚）
      this._kwScrollDone = false
    },
    currentRunId() {
      this.userScrolling = false
      this.autoScroll = true
      this.showScrollButton = false
      this.$nextTick(() => this.scrollToBottom())
    },
    isSwitchingSession() {
      this.userScrolling = false
      this.autoScroll = true
      this.showScrollButton = false
      this.resetNarrationBuffer()
      this.$nextTick(() => this.scrollToBottom())
    },
    /**
     * 缓冲判定的候选变化：
     *   - 换了消息（或清空）→ 取消旧兜底计时器；旧消息的去向已由结构决定
     *     （来了工具调用 → 反向扫描收进容器；回合结束 → 留在气泡），无需落锁；
     *   - 来了新候选 → 先看内容是否已"像正文"：是 → 直接落锁到气泡（不隐藏）；
     *     否 → 隐藏（bufferedNarrationId = id），并起一个 NARRATION_FALLBACK_MS 的
     *     兜底计时器：到点仍无工具调用且还没像正文 → 按正文落定到气泡。
     * 流式 delta 只改消息内容、不改候选身份（同一消息引用），不会重置兜底计时器；
     * 内容越过阈值由 bufferedNarrationLooksBody 的 watch 负责。
     */
    bufferedNarrationCandidate: {
      immediate: true,
      handler(msg, oldMsg) {
        const newId = msg && msg.id != null ? msg.id : null
        const oldId = oldMsg && oldMsg.id != null ? oldMsg.id : null
        if (newId === oldId) return
        if (this.narrationBufferTimer) {
          clearTimeout(this.narrationBufferTimer)
          this.narrationBufferTimer = null
        }
        // 新候选一出现就已是正文 → 直接落锁，不隐藏（避免正文开头在容器里闪一下）
        if (newId != null && looksLikeBody(typeof msg.content === 'string' ? msg.content : '')) {
          this.bufferedNarrationId = null
          if (this.lockedBodyIds.indexOf(newId) === -1) this.lockedBodyIds.push(newId)
          return
        }
        this.bufferedNarrationId = newId
        if (newId == null) return
        const timerId = newId
        this.narrationBufferTimer = setTimeout(() => {
          this.narrationBufferTimer = null
          if (this.bufferedNarrationId !== timerId) return
          this.bufferedNarrationId = null
          // 兜底超时：一直不像正文又没等到工具调用 → 按正文落定到气泡
          if (this.lockedBodyIds.indexOf(timerId) === -1) {
            this.lockedBodyIds.push(timerId)
          }
        }, NARRATION_FALLBACK_MS)
      }
    },
    /**
     * 隐藏中的叙述内容越过阈值 → 立即落锁为正文，在气泡里流式（从隐藏直接到气泡，
     * 从未进过容器）。流式 delta 不断触发本计算，只有 false→true 这一跳才落锁。
     */
    bufferedNarrationLooksBody(val) {
      if (!val) return
      const id = this.bufferedNarrationId
      if (id == null) return
      if (this.narrationBufferTimer) {
        clearTimeout(this.narrationBufferTimer)
        this.narrationBufferTimer = null
      }
      this.bufferedNarrationId = null
      if (this.lockedBodyIds.indexOf(id) === -1) this.lockedBodyIds.push(id)
    }
  },
  mounted() {
    this.lastMsgCount = this.messages ? this.messages.length : 0
    this.startAutoScrollTimer()
    document.addEventListener('cosmo-claw-scroll-bottom', this.handleForceScrollBottom)
    document.addEventListener('cosmo-claw-force-scroll', this.handleForceScrollToBottom)
    this.rehydrateCeoStrategyCards()
    this.scheduleKeywordHighlight()
  },
  created() {
    // 搜索关键词高亮：创建的 <mark> 与定时器不参与响应式
    this._kwMarks = []
    this._kwHighlightTimer = null
    this._kwRetryTimer = null
    this._kwDestroyed = false
    // 每个会话是否已经自动滚到首个高亮（只滚一次，避免流式重渲染反复拉扯）
    this._kwScrollDone = false
    this._kwScrollTimer = null
  },
  beforeDestroy() {
    this._kwDestroyed = true
    if (this._kwHighlightTimer) clearTimeout(this._kwHighlightTimer)
    if (this._kwRetryTimer) clearTimeout(this._kwRetryTimer)
    if (this._kwScrollTimer) clearTimeout(this._kwScrollTimer)
    this.clearKeywordHighlight()
    if (this.scrollTimer) clearInterval(this.scrollTimer)
    if (this.autoScrollRaf) cancelAnimationFrame(this.autoScrollRaf)
    this.stopProcessingStatus()
    this.resetNarrationBuffer()
    document.removeEventListener('cosmo-claw-scroll-bottom', this.handleForceScrollBottom)
    document.removeEventListener('cosmo-claw-force-scroll', this.handleForceScrollToBottom)
  },
  methods: {
    /** 启动处理中状态行：按用户消息时间戳起算（100ms 刷新，展示到 0.1 秒），并起 10s 文案轮换。 */
    startProcessingStatus() {
      this.stopProcessingStatus()
      this.processingMessageIndex = Math.floor(Math.random() * PROCESSING_MESSAGES.length)
      const start = this.processingStartTime || Date.now()
      const tick = () => {
        this.processingElapsed = Math.max(0, (Date.now() - start) / 1000)
      }
      tick()
      this.processingTimer = setInterval(tick, 100)
      this.processingRotateTimer = setInterval(() => {
        this.rotateProcessingMessage()
      }, PROCESSING_ROTATE_MS)
    },
    stopProcessingStatus() {
      if (this.processingTimer) {
        clearInterval(this.processingTimer)
        this.processingTimer = null
      }
      if (this.processingRotateTimer) {
        clearInterval(this.processingRotateTimer)
        this.processingRotateTimer = null
      }
    },
    // 随机换一条文案，且保证与当前不同（单条文案池不轮换）
    rotateProcessingMessage() {
      const total = PROCESSING_MESSAGES.length
      if (total < 2) return
      let next = this.processingMessageIndex
      while (next === this.processingMessageIndex) {
        next = Math.floor(Math.random() * total)
      }
      this.processingMessageIndex = next
    },
    /** 清空缓冲判定状态（计时器 / 在途候选 / 正文锁定）。切换会话与组件销毁时调用。 */
    resetNarrationBuffer() {
      if (this.narrationBufferTimer) {
        clearTimeout(this.narrationBufferTimer)
        this.narrationBufferTimer = null
      }
      this.bufferedNarrationId = null
      this.lockedBodyIds = []
    },
    /**
     * 瀵硅瘽妗嗘敹璧峰啀鎵撳紑鍚庯紝CEO 鎴樼暐鍗＄殑 v-html 閲嶆柊娓叉煋浼氫涪澶憋細
     * - 宸叉彮绀?section 鐨勫彲瑙佺姸鎬侊紙display:none 鍒濆鏍峰紡锛?
     * - verdict 鎵撳瓧鏈哄啓鍏ョ殑鏂囧瓧
     * 杩欓噷寮哄埗鎭㈠锛氭墍鏈?data-ceo-card="shell" 鍐呯殑 section 鍏ㄩ儴鏄剧ず锛?
     * 骞跺洖濉?verdict 鎵撳瓧鏈烘枃瀛椼€?
     */
    rehydrateCeoStrategyCards() {
      const root = this.$refs.container
      if (!root) return
      // 1) 寮哄埗鎵€鏈?CEO 鍗＄墖鍐呯殑 section 淇濇寔鏄剧ず
      //    鐢变簬 v-html 閲嶆柊娓叉煋鍚?data-revealed 灞炴€у凡涓㈠け锛?
      //    杩欓噷鐩存帴鐢?CSS 閫夋嫨鍣ㄥ懡涓?data-ceo-card="shell" 鍐呯殑鎵€鏈?section
      root.querySelectorAll('[data-ceo-card="shell"] .ceo-strategy-section').forEach(function (el) {
        el.style.display = 'block'
        el.style.opacity = '1'
        el.style.transform = 'translateY(0)'
        // 鍚屾椂闅愯棌钂欑増閬僵锛堥伩鍏?loading 钂欑増娈嬬暀鎸′綇鍐呭锛?
        var mask = el.querySelector('.ceo-strategy-mask')
        if (mask) mask.style.display = 'none'
      })
      // 2) 鍥炲～ verdict 鏂囨湰锛堟潵鑷?data-ceo-strategy-text 灞炴€э紝閬垮厤 v-html 閲嶆柊娓叉煋鍚庝涪澶憋級
      root.querySelectorAll('[data-ceo-strategy-text]').forEach(function (el) {
        if (!el.textContent || el.textContent.length === 0) {
          el.textContent = el.getAttribute('data-ceo-strategy-text')
        }
      })
    },
    cleanupOrphanThinkingToggles() {
      const root = this.$refs.container
      if (!root) return
      root.querySelectorAll('[data-thinking-toggle="true"]').forEach(el => {
        const insideMessage = el.closest('.message-item')
        if (!insideMessage) {
          if (el.parentElement) el.parentElement.removeChild(el)
        }
      })
    },
    handleContainerClick(e) {
      const toggleBar = e.target.closest('[data-toggle-id]')
      if (!toggleBar) return
      e.stopPropagation()
      const toggleId = toggleBar.getAttribute('data-toggle-id')
      this.toggleThinkingContent(toggleId, toggleBar)
    },
    toggleThinkingContent(toggleId, barEl) {
      const content = document.getElementById(toggleId + '-content')
      if (!content) {
        console.warn('[CosmoClaw] toggleThinkingContent: content not found for', toggleId)
        return
      }
      const isCollapsed = content.style.maxHeight === '0px' || content.style.maxHeight === ''
      if (isCollapsed) {
        // 展开
        content.style.maxHeight = '800px'
        content.style.opacity = '1'
        content.style.overflow = ''
        content.style.padding = '10px'
        const label = barEl.querySelector('[data-toggle-label]')
        if (label) label.textContent = '收起推理'
        const arrow = barEl.querySelector('[data-toggle-arrow]')
        if (arrow) arrow.style.transform = 'rotate(0deg)'
      } else {
        // 收起
        content.style.maxHeight = '0px'
        content.style.opacity = '0'
        content.style.overflow = 'hidden'
        content.style.padding = '0 16px'
        const label = barEl.querySelector('[data-toggle-label]')
        if (label) label.textContent = '展开查看'
        const arrow = barEl.querySelector('[data-toggle-arrow]')
        if (arrow) arrow.style.transform = 'rotate(-90deg)'
      }
    },
    /**
     * 程序化贴底：写 scrollTop 的同时把 lastScrollTop 同步成写入后的位置。
     *
     * 这样这次写入产生的 scroll 事件在 handleScroll 里看到的是"位置没变（或向下）"，
     * 不会被误判为"用户上滚接管"。所有自动贴底都必须走这里，不要直接写 scrollTop。
     */
    pinToBottom() {
      const el = this.$refs.container
      if (!el) return
      el.scrollTop = el.scrollHeight
      this.lastScrollTop = el.scrollTop
    },
    // 刻度尺导航前调用：解除 rAF 自动贴底锁定，否则 pinToBottom 会在下一帧把 scrollIntoView 的滚动拉回底部
    pauseAutoScroll() {
      this.userScrolling = true
      this.autoScroll = false
      // rail 导航的 smooth 滚动起点在底部，开始的几帧距底仍 ≤4px，
      // 会被 handleScroll 的"回到底部恢复跟随"分支误判并立刻解锁 → 加时间窗，窗内禁止恢复
      this.railNavLockUntil = Date.now() + 800
    },
    // ---- 会话搜索关键词高亮：渲染后包 <mark>，不改 markdown 源码 ----
    scheduleKeywordHighlight(delay = 50) {
      if (this._kwDestroyed) return
      if (this._kwHighlightTimer) clearTimeout(this._kwHighlightTimer)
      this._kwHighlightTimer = setTimeout(() => {
        this._kwHighlightTimer = null
        this.applyKeywordHighlight(0)
      }, delay)
    },
    applyKeywordHighlight(attempt) {
      if (this._kwDestroyed) return
      this.clearKeywordHighlight()
      const keyword = String(this.searchKeyword || '').trim()
      if (!keyword) return
      const container = this.$refs.container
      if (!container) return
      // 只处理 v-html 渲染出的纯文本容器：用户正文、追问正文、富文本、助手 markdown 文本段。
      // chart/pagelist/card 等自定义组件由 Vue 组件渲染，文本节点归组件管理，绝不能插入 <mark>
      const items = container.querySelectorAll(
        [
          '.message-item.message-user .user-text-content',
          '.message-item.message-user .questioning-text',
          '.rich-html-content',
          '.message-item.message-assistant [data-kw-text]'
        ].join(',')
      )
      let matched = false
      items.forEach((el) => {
        if (this.highlightKeywordInElement(el, keyword)) matched = true
      })
      // 首次命中后定位到第一个高亮，省得用户自己往上翻
      if (matched && !this._kwScrollDone) {
        this.scrollToFirstMatch()
      }
      // 历史消息异步加载，DOM 晚于路由出现：未命中时短轮询重试
      if (!matched && attempt < 20) {
        this._kwRetryTimer = setTimeout(() => {
          this._kwRetryTimer = null
          this.applyKeywordHighlight(attempt + 1)
        }, 150)
      }
    },
    /**
     * 滚动到第一个高亮位置。每个会话只执行一次（_kwScrollDone），
     * 否则流式 chunk 触发的重复高亮会把视图反复拉走。
     */
    scrollToFirstMatch() {
      const box = this.$refs.container
      if (!box || !this._kwMarks || !this._kwMarks.length) return
      // 会话切换中命中的可能是上一个会话的残留 DOM，不消耗这次定位机会
      if (this.isSwitchingSession) return
      this._kwScrollDone = true
      // 必须先解除自动贴底，否则 rAF 会在下一帧把视图拉回底部
      this.pauseAutoScroll()
      let appliedTop = null
      const apply = () => {
        const el = this.$refs.container
        const mark = this._kwMarks && this._kwMarks[0]
        if (!el || !mark) return
        const top =
          el.scrollTop +
          (mark.getBoundingClientRect().top - el.getBoundingClientRect().top) -
          80
        const max = Math.max(0, el.scrollHeight - el.clientHeight)
        el.scrollTop = Math.min(Math.max(top, 0), max)
        appliedTop = el.scrollTop
        // 程序化写入必须同步 lastScrollTop，否则这次写入会被 handleScroll 当成用户上滚
        this.lastScrollTop = el.scrollTop
      }
      apply()
      // 历史消息 / 图片 / 卡片异步撑高会改变位置，稍后校正一次；
      // 期间用户若已手动滚动（scrollTop 变了）就不再打扰
      if (this._kwScrollTimer) clearTimeout(this._kwScrollTimer)
      this._kwScrollTimer = setTimeout(() => {
        this._kwScrollTimer = null
        const el = this.$refs.container
        if (this._kwDestroyed || !el) return
        if (appliedTop === null || Math.abs(el.scrollTop - appliedTop) > 2) return
        apply()
      }, 300)
    },
    highlightKeywordInElement(root, keyword) {
      if (!root) return false
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
          if (!node.nodeValue || !node.nodeValue.trim()) {
            return NodeFilter.FILTER_REJECT
          }
          const parent = node.parentNode
          if (!parent || parent.nodeType !== 1) {
            return NodeFilter.FILTER_REJECT
          }
          // 代码块、链接、表单控件、媒体等不改 DOM
          if (
            parent.closest &&
            parent.closest(
              'pre, code, script, style, textarea, input, select, option, button, a, svg, canvas, video, audio, mark, [contenteditable="true"], [data-no-highlight]'
            )
          ) {
            return NodeFilter.FILTER_REJECT
          }
          // 子组件（SOP 卡片、MdCard、CardList 等）由 Vue 管理，
          // 插入 <mark> 会破坏其 vnode/text 节点结构，直接跳过
          let el = parent
          while (el && el !== root) {
            if (el.__vue__) return NodeFilter.FILTER_REJECT
            el = el.parentNode
          }
          return NodeFilter.FILTER_ACCEPT
        }
      })
      const textNodes = []
      while (walker.nextNode()) textNodes.push(walker.currentNode)
      const lowerKeyword = keyword.toLowerCase()
      let matched = false
      textNodes.forEach((node) => {
        const text = node.nodeValue
        const lowerText = text.toLowerCase()
        let from = lowerText.indexOf(lowerKeyword)
        if (from === -1) return
        matched = true
        const frag = document.createDocumentFragment()
        let cursor = 0
        while (from !== -1) {
          if (from > cursor) {
            frag.appendChild(document.createTextNode(text.slice(cursor, from)))
          }
          const mark = document.createElement('mark')
          mark.className = 'kw-highlight'
          mark.textContent = text.slice(from, from + keyword.length)
          this._kwMarks.push(mark)
          frag.appendChild(mark)
          cursor = from + keyword.length
          from = lowerText.indexOf(lowerKeyword, cursor)
        }
        if (cursor < text.length) {
          frag.appendChild(document.createTextNode(text.slice(cursor)))
        }
        node.parentNode.replaceChild(frag, node)
      })
      return matched
    },
    clearKeywordHighlight() {
      if (this._kwRetryTimer) {
        clearTimeout(this._kwRetryTimer)
        this._kwRetryTimer = null
      }
      if (!this._kwMarks || this._kwMarks.length === 0) return
      this._kwMarks.forEach((mark) => {
        const parent = mark.parentNode
        if (!parent) return
        parent.replaceChild(document.createTextNode(mark.textContent || ''), mark)
        parent.normalize()
      })
      this._kwMarks = []
    },
    // demo 流程在流式打字期间高频派发的"跟随贴底"事件。
    // 必须尊重用户接管锁:用户主动上滚翻看时(userScrolling=true)一律忽略,绝不硬拽回底部。
    handleForceScrollBottom(e) {
      if (this.userScrolling) return
      const force = e && e.detail && e.detail.force
      if (!force && !this.autoScroll) return
      this.autoScroll = true
      this.showScrollButton = false
      this.$nextTick(() => {
        if (this.userScrolling) return
        this.pinToBottom()
      })
    },
    // 显式"强制回到底部"（切换会话 / 新一轮开始等）：解除用户接管锁后贴底。
    handleForceScrollToBottom() {
      this.userScrolling = false
      this.autoScroll = true
      this.showScrollButton = false
      this.$nextTick(() => {
        this.pinToBottom()
      })
    },
    scrollToBottom() {
      // 显式回到底部(点击按钮 / 强制事件):解除用户接管锁并恢复自动跟随
      this.userScrolling = false
      this.autoScroll = true
      this.showScrollButton = false
      this.$nextTick(() => {
        this.pinToBottom()
      })
    },
    // 用户主动滚动意图(滚轮/触摸):向上滚立即置起"用户接管锁"并停止自动贴底,不经过距底阈值判断。
    // 这样能打破"滚轮位移被 rAF 每帧贴底 + 流式 chunk 触发 messages watch 反复打开跟随"的拉锯死锁——
    // 用户一伸手就把跟随彻底关掉,直到自己滚回底部附近才由 handleScroll 解锁。
    handleUserScrollIntent(e) {
      // wheel: deltaY < 0 表示向上滚。用户一向上滚立即接管,不经过距底阈值,滚动即刻生效。
      // 但必须确认容器此刻真的"还能往上滚"(scrollTop > 0):已贴底时用户反向拨一下滚轮,
      // 视图根本没动,却把跟随永久关掉,会让后续输出停在原地。
      if (e && typeof e.deltaY === 'number') {
        if (e.deltaY < 0) {
          const box = this.$refs.container
          if (box && box.scrollTop > 0) {
            this.userScrolling = true
            this.autoScroll = false
            this.showScrollButton = true
          }
        }
        return
      }
      // touchmove 无 deltaY:比对当前 scrollTop 与上次记录,若变小说明向上滑 → 接管
      const el = this.$refs.container
      if (el) {
        if (typeof this.lastTouchScrollTop === 'number' && el.scrollTop < this.lastTouchScrollTop - 1) {
          this.userScrolling = true
          this.autoScroll = false
          this.showScrollButton = true
        }
        this.lastTouchScrollTop = el.scrollTop
      }
    },
    handleScroll() {
      const el = this.$refs.container
      if (!el) return
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
      // 接管判定只看"用户真的把视图往上挪了":
      // 内容一次性长一大块(先出表格 / 富卡片)时,我们自己贴底的写入与其 scroll 事件之间
      // 会夹着这次高度增长,此时 scrollTop 没变、distanceFromBottom 却突然变大——
      // 旧逻辑据此判定"用户上滚介入",把自动跟随永久关掉,后续输出就停在原地。
      const wentUp = el.scrollTop < this.lastScrollTop - 1
      this.lastScrollTop = el.scrollTop
      if (distanceFromBottom > 50 && wentUp) {
        // 明显离开底部且确实是用户上移:接管滚动,关闭自动贴底,并按距离决定是否显示回底按钮
        this.userScrolling = true
        this.autoScroll = false
        this.showScrollButton = distanceFromBottom > 100
      } else if (distanceFromBottom <= 4) {
        // 仅当"几乎完全贴底"(≤4px)时才解除用户接管锁并恢复自动跟随。
        // 用更严格的解锁阈值(而非 50px),避免用户轻微上滚(仍在 50px 内)时被误判为"回到底部"
        // 而立即解锁、随后被 force 事件拽回——那正是"滚不动"的残留成因。
        // 例外:rail 刻度导航的时间窗内禁止恢复——smooth 滚动从贴底起步,前几帧距底 ≤4px 属误判。
        if (!(this.railNavLockUntil && Date.now() < this.railNavLockUntil)) {
          this.userScrolling = false
          this.autoScroll = true
          this.showScrollButton = false
        }
      }
      // 4~50px 的中间地带:保持当前 userScrolling 状态不变(用户正在底部附近微调,尊重其接管)
      this.lastTouchScrollTop = el.scrollTop
      // 滚动时驱动"待收起且已完全滚出可视区"的工具卡片收起(手动上滑场景)
      this.collapseScrolledOutProcessCards()
    },
    /** 过程行右侧"可打开"箭头（结果里带 MEDIA:）→ 冒泡给会话层去开文件。 */
    onOpenTraceMedia(row) {
      this.$emit('open-media', row)
    },
    /** 过程行点击 → 打开全屏详情浮层（目标客户端的 ActivityCardsOverlay 等价物）。 */
    onOpenTraceDetail(msg) {
      this.detailMsg = msg || null
    },
    /** 思考 / 叙述行点击 → 打开全文浮层（目标客户端的 Activity 文本浮层）。 */
    onOpenTraceText(payload) {
      this.textDetail = (payload && payload.text) ? payload : null
    },
    // 巡检所有过程卡片（trace / 旧的工具调用卡片），收起"已失去 isLast、且已完全滚出聊天可视区"的卡片。
    // 由父组件在每次滚动 / 自动贴底时统一驱动,是唯一的可靠收起路径(不依赖子组件自身回调,消除"全部不收起")。
    // 因为只收"完全滚出可视区"的卡片,用户绝不会看到收起过程 → 不会当面塌陷 → 不抖。
    // 若本轮有卡片收起且当前处于自动贴底,则同帧补一次贴底,消除高度变化带来的位置跳动。
    collapseScrolledOutProcessCards() {
      const children = this.$children || []
      const names = ['CosmoClawProcessTrace', 'CosmoClawToolGroup']
      let collapsed = false
      for (let i = 0; i < children.length; i++) {
        const c = children[i]
        if (
          c &&
          c.$options &&
          names.indexOf(c.$options.name) !== -1 &&
          typeof c.collapseIfScrolledOut === 'function'
        ) {
          if (c.collapseIfScrolledOut()) collapsed = true
        }
      }
      // 仅在自动跟随且用户未接管滚动时才补贴底;否则会把用户翻看的视图拽回底部造成拉扯
      if (collapsed && this.autoScroll && !this.userScrolling) {
        this.pinToBottom()
      }
    },
    startAutoScrollTimer() {
      // 逐帧(requestAnimationFrame)自动贴底:内容高度连续增长时,贴底也逐帧连续跟随,
      // 避免 200ms 轮询"每拍硬跳一大段"造成的抖动(工具卡片内工具流式追加时高度持续变化)。
      const tick = (ts) => {
        // 用户接管滚动(userScrolling)时,rAF 绝不写 scrollTop——这是消除向上滚拉扯的核心闸门。
        // 其余情况持续贴底:autoScroll / userScrolling 全程成对设置,只看 userScrolling 即可,
        // 且 pinToBottom 会同步 lastScrollTop,使程序化贴底产生的 scroll 事件不被误判为用户上滚。
        if (!this.userScrolling) this.pinToBottom()
        // 收起巡检开销较大(几何测量),节流到约 200ms 一次即可,不必每帧跑
        if (!this.lastCollapseCheckAt || ts - this.lastCollapseCheckAt >= 200) {
          this.lastCollapseCheckAt = ts
          this.collapseScrolledOutProcessCards()
        }
        this.autoScrollRaf = requestAnimationFrame(tick)
      }
      this.autoScrollRaf = requestAnimationFrame(tick)
    }
  }
}
</script>

<style lang="scss" scoped>
/* 会话搜索关键词高亮（运行期插入的 <mark>，需要 ::v-deep 穿透 scoped） */
::v-deep .kw-highlight {
  background: #ffe58f;
  color: inherit;
  padding: 0;
  border-radius: 2px;
}
/* 外层 root 容器:flex column,负责撑满父元素,position:relative 给 rail 做定位参考 */
.message-list-root {
  flex: 1;
  display: flex;
  min-height: 0;
  min-width: 0;
  position: relative;
}

/* 滚动容器:flex 子项,负责滚动,不做 position:relative(给外层 root 统一管理) */
.chat-scroll-wrapper {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  // scrollbar-width/color 必须为 auto，否则 ::-webkit-scrollbar-* 整条链不生效
  scrollbar-width: auto;
  scrollbar-color: auto;
}

/* 内层消息流容器:800px 居中,与输入框同宽,不负责滚动 */
.message-list-container {
  width: 100%;
  max-width: 880px;
  margin-left: auto;
  margin-right: auto;
  box-sizing: border-box;
  padding: 16px 20px;
  // 避开顶部统一头部(.chat-header 高度约 60px) + 间距
  // margin-top: 70px;
}

/* 处理中状态行：3×3 方块 spinner + 轮换文案 + 已用时（对齐客户端 ProcessingIndicator） */
.processing-status {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 4px;
  margin: -8px 0 12px;
  font-size: 13px;
  line-height: 20px;
  color: #909399;

  &__spinner {
    display: inline-grid;
    grid-template-columns: repeat(3, 1fr);
    width: 1em;
    height: 1em;
    gap: 0.08em;
    font-size: 10px;
    color: #a8abb2;
    flex-shrink: 0;
  }

  &__cube {
    background-color: currentColor;
    animation: processing-grid 1.3s infinite ease-in-out;
    transform: scale3d(0.5, 0.5, 1);

    &:nth-child(1) { animation-delay: 0.2s; }
    &:nth-child(2) { animation-delay: 0.3s; }
    &:nth-child(3) { animation-delay: 0.4s; }
    &:nth-child(4) { animation-delay: 0.1s; }
    &:nth-child(5) { animation-delay: 0.2s; }
    &:nth-child(6) { animation-delay: 0.3s; }
    &:nth-child(7) { animation-delay: 0s; }
    &:nth-child(8) { animation-delay: 0.1s; }
    &:nth-child(9) { animation-delay: 0.2s; }
  }

  &__label {
    display: inline-flex;
    align-items: center;
    height: 20px;
  }

  &__text {
    display: inline-block;
  }

  &__elapsed {
    margin-left: 4px;
    color: rgba(144, 147, 153, 0.6);
    font-variant-numeric: tabular-nums;
  }
}

@keyframes processing-grid {
  0%, 70%, 100% { transform: scale3d(0.5, 0.5, 1); }
  35% { transform: scale3d(0, 0, 0); }
}

.processing-fade-enter-active,
.processing-fade-leave-active {
  transition: opacity 0.4s ease-in-out;
}

.processing-fade-enter,
.processing-fade-leave-to {
  opacity: 0;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #c0c4cc;

  .empty-icon {
    font-size: 48px;
    margin-bottom: 12px;
  }

  .empty-text {
    font-size: 14px;
  }
}

.scroll-to-bottom {
  position: sticky;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  margin: 0 auto;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  i {
    font-size: 16px;
    color: #646a73;
  }
}

/* 消息导航刻度尺：absolute 紧贴 message-list-root 右边缘（双类选择器提升优先级，压过 Rail 根元素自带的 position: relative） */
.message-list-root .msg-rail {
  position: absolute;
  top: 0;
  right: 12px;
  height: 100%;
  pointer-events: auto;
  flex-shrink: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* 滚动条:默认隐藏,鼠标悬浮容器时显示。
   轨道 10px,滑块视觉 8px:1px 透明边框把两侧各收进 1px */
.chat-scroll-wrapper::-webkit-scrollbar {
  width: 10px !important;
  height: 10px !important;
}

.chat-scroll-wrapper::-webkit-scrollbar-track {
  background: transparent;
  box-shadow: none;
  border: none;
}

.chat-scroll-wrapper::-webkit-scrollbar-thumb {
  background-color: transparent !important;
  background-clip: padding-box !important;
  border: 1px solid transparent;
  border-radius: 5px !important;
  box-shadow: none !important;
}

/* 由 JS 维护 class,规避 CSS :hover 在滚动到端点时的粘滞 */
.chat-scroll-wrapper.is-scroll-hover::-webkit-scrollbar-thumb {
  background-color: rgba(156, 167, 248, 0.2) !important;
  background-clip: padding-box !important;
  border: 1px solid transparent;
  border-radius: 5px !important;
}
</style>
